import type { CapacityAllocationMap, CalendarEntry } from '@/types/capacityCalendar';
import type { CapacityPlanParseResult, CapacityPlanProductionLine, CapacityPlanWeek } from '@/types/capacityImport';

export interface PendingCell {
  rowId: string; // entry id `${vendorId}:${capacityTypeId}` (real or ghost)
  vendorId: string;
  capacityTypeId: string;
  weekStart: string; // Monday ISO date
  kind: 'new' | 'changed';
  currentCapacity: number | null; // null for 'new'
  nextCapacity: number;
  // Only set on a synthetic month rollup (aggregatePendingInRange): summing weeks can hide a cut
  // behind a bigger raise, so the rollup carries the per-week verdict rather than re-deriving it.
  containsDecrease?: boolean;
}

export interface GhostRow {
  rowId: string; // `${vendorId}:${capacityTypeId}`
  vendorId: string;
  vendorCode: string;
  capacityTypeId: string;
  typeCode: string; // parse line's terminationTypeCode
  typeLabel: string;
}

// A parsed week whose value already equals the calendar's — display-only "footprint" marker
// (spec: CapacityPeriodCell's neutral ring + SAME badge) so a fully-matching file still shows
// what was checked. Never excludable and never applied — there's nothing to apply, the values
// already agree — so it's kept out of `cells`/buildOverwriteTargets/buildApprovedProductionLines.
export interface MatchedCell {
  rowId: string;
  weekStart: string; // Monday ISO date
  capacity: number;
}

export interface ImportDiff {
  cells: PendingCell[];
  ghostRows: GhostRow[];
  matchedCells: MatchedCell[];
  counts: { added: number; changed: number; attention: number; matched: number; decreased: number };
}

// A changed cell whose capacity goes DOWN. Kept as an exported predicate rather than an inline
// comparison because the count and the per-cell styling must agree — the calendar preview says
// "N decrease(s)" and then has to mark exactly those N cells.
export function isDecrease(cell: PendingCell): boolean {
  if (cell.containsDecrease === true) {
    return true;
  }
  return cell.kind === 'changed' && cell.currentCapacity !== null && cell.nextCapacity < cell.currentCapacity;
}

const MS_PER_DAY = 86_400_000;
const DAYS_PER_WEEK = 7;

// Matches the backend's weekStart normalization (copied from capacityImportStore.ts's
// module-local mondayOf) so overlap detection lines up 1:1. Kept as a plain util here —
// this file must not import a store.
function mondayOf(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  const offset = (date.getUTCDay() + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
  return new Date(date.getTime() - offset * MS_PER_DAY).toISOString().slice(0, 'YYYY-MM-DD'.length);
}

// A parsed line's calendar row identity — same shape as PendingCell.rowId / GhostRow.rowId.
// Shared by buildImportDiff and buildApprovedProductionLines so both resolve a line's
// row the same way.
function lineRowId(line: Pick<CapacityPlanProductionLine, 'vendorId' | 'terminationTypeId'>): string | null {
  return line.vendorId && line.terminationTypeId ? `${line.vendorId}:${line.terminationTypeId}` : null;
}

// Monday-normalized weekStart for each parse week, in parse.weeks order — shared between
// buildImportDiff (matching against the calendar) and buildApprovedProductionLines
// (matching against excluded cell keys) so both use identical week-index resolution.
function weekStartsOf(weeks: CapacityPlanWeek[]): string[] {
  return weeks.map((week) => mondayOf(week.startDate));
}

// Builds the preview diff between a freshly parsed plan and what's already on the calendar.
// Mirrors capacityImportStore's bucketDiffs vendor-resolution rules, but at line granularity
// (no cross-line bucket summing) since this preview edits/overwrites per parsed line+week.
export function buildImportDiff(parse: CapacityPlanParseResult, entries: CalendarEntry[], allocations: CapacityAllocationMap): ImportDiff {
  const entryById = new Map(entries.map((entry) => [entry.id, entry]));
  const vendorIdsInEntries = new Set(entries.map((entry) => entry.vendorId).filter((id): id is string => Boolean(id)));
  const ghostRowsByRowId = new Map<string, GhostRow>();
  const cells: PendingCell[] = [];
  const matchedCells: MatchedCell[] = [];
  const weekStarts = weekStartsOf(parse.weeks);
  let added = 0;
  let changed = 0;
  let attention = 0;
  let matched = 0;
  let decreased = 0;

  parse.productionLines.forEach((line) => {
    if (line.skipped) return;

    const { vendorId, terminationTypeId } = line;
    if (!vendorId || !terminationTypeId) {
      attention += 1;
      return;
    }
    if (!vendorIdsInEntries.has(vendorId)) {
      attention += 1;
      return;
    }

    const rowId = lineRowId(line) as string; // vendorId/terminationTypeId already checked above
    if (!entryById.has(rowId) && !ghostRowsByRowId.has(rowId)) {
      ghostRowsByRowId.set(rowId, {
        rowId,
        vendorId,
        vendorCode: line.vendorCode ?? '',
        capacityTypeId: terminationTypeId,
        typeCode: line.terminationTypeCode,
        typeLabel: line.terminationTypeCode,
      });
    }

    const capacityValues = line.metrics.find((metric) => metric.key === 'capacity')?.values ?? [];
    weekStarts.forEach((_weekStart, weekIndex) => {
      const nextCapacity = capacityValues[weekIndex] ?? null;
      if (nextCapacity === null) return;

      const weekStart = weekStarts[weekIndex];
      const existingAllocation = allocations[rowId]?.[weekStart] ?? null;
      const currentCapacity = existingAllocation?.capacity ?? null;
      if (existingAllocation && currentCapacity === nextCapacity) {
        // Unchanged: the file's value already matches the calendar. Collected instead of
        // dropped so the preview can still show its footprint (see MatchedCell) — nothing to
        // apply here, so this never becomes a PendingCell.
        matchedCells.push({ rowId, weekStart, capacity: nextCapacity });
        matched += 1;
        return;
      }

      const kind: PendingCell['kind'] = existingAllocation ? 'changed' : 'new';
      const cell: PendingCell = {
        rowId,
        vendorId,
        capacityTypeId: terminationTypeId,
        weekStart,
        kind,
        currentCapacity: existingAllocation ? currentCapacity : null,
        nextCapacity,
      };
      cells.push(cell);
      if (kind === 'new') {
        added += 1;
      } else {
        changed += 1;
        if (isDecrease(cell)) decreased += 1;
      }
    });
  });

  return { cells, ghostRows: [...ghostRowsByRowId.values()], matchedCells, counts: { added, changed, attention, matched, decreased } };
}

export type ParsedComparisonStatus = 'decrease' | 'increase' | 'new' | 'same' | 'unresolved';

export interface ParsedWeekComparison {
  status: ParsedComparisonStatus;
  currentCapacity: number | null; // null unless status is decrease/increase/same
  delta: number | null; // next − current; null unless there was a current to subtract from
}

// Compares a stored parse against the calendar AS IT IS NOW — not as it was at parse time, which
// nothing records. Same classification rules as buildImportDiff, but read-only and keyed
// `${lineIndex}:${weekIndex}` so a renderer walking parse.productionLines/weeks by position can
// look a cell up without re-deriving row ids or week normalization. Used for records nobody
// applied, where there is no stored diff to show.
export function buildParsedComparison(parse: CapacityPlanParseResult, entries: CalendarEntry[], allocations: CapacityAllocationMap): Map<string, ParsedWeekComparison> {
  const vendorIdsInEntries = new Set(entries.map((entry) => entry.vendorId).filter((id): id is string => Boolean(id)));
  const weekStarts = weekStartsOf(parse.weeks);
  const comparisons = new Map<string, ParsedWeekComparison>();

  parse.productionLines.forEach((line, lineIndex) => {
    const rowId = lineRowId(line);
    const capacityValues = line.metrics.find((metric) => metric.key === 'capacity')?.values ?? [];

    weekStarts.forEach((weekStart, weekIndex) => {
      const nextCapacity = capacityValues[weekIndex] ?? null;
      if (nextCapacity === null) return;

      const key = `${lineIndex}:${weekIndex}`;
      if (!rowId || !line.vendorId || !vendorIdsInEntries.has(line.vendorId)) {
        comparisons.set(key, { status: 'unresolved', currentCapacity: null, delta: null });
        return;
      }

      const currentCapacity = allocations[rowId]?.[weekStart]?.capacity ?? null;
      if (currentCapacity === null) {
        comparisons.set(key, { status: 'new', currentCapacity: null, delta: null });
        return;
      }

      const delta = nextCapacity - currentCapacity;
      const status: ParsedComparisonStatus = delta === 0 ? 'same' : delta < 0 ? 'decrease' : 'increase';
      comparisons.set(key, { status, currentCapacity, delta });
    });
  });

  return comparisons;
}

// changed cells only — confirm payload field name is frozen
export function buildOverwriteTargets(diff: ImportDiff): { vendorId: string; terminationTypeId: string; weekStart: string }[] {
  return diff.cells.filter((cell) => cell.kind === 'changed').map((cell) => ({ vendorId: cell.vendorId, terminationTypeId: cell.capacityTypeId, weekStart: cell.weekStart }));
}

// Filters an ImportDiff down to the cells NOT matching `${rowId}:${weekStart}` in excludedKeys,
// recomputing added/changed counts and dropping any ghost row left with no remaining cells.
// attention is untouched — it counts unresolvable parse lines, not excludable cells.
// matchedCells/counts.matched pass through untouched too — a matched cell applies nothing, so
// there's nothing for the × button to exclude and no weekStart of theirs can appear in
// excludedKeys. Shared by excludePendingCell (one new key at a time, against the diff as it
// currently stands) and the store's refresh-time recompute (the full accumulated set against a
// freshly rebuilt diff).
export function applyExclusions(diff: ImportDiff, excludedKeys: ReadonlySet<string>): ImportDiff {
  if (excludedKeys.size === 0) return diff;

  const cells = diff.cells.filter((cell) => !excludedKeys.has(`${cell.rowId}:${cell.weekStart}`));
  const remainingRowIds = new Set(cells.map((cell) => cell.rowId));
  return {
    cells,
    ghostRows: diff.ghostRows.filter((ghost) => remainingRowIds.has(ghost.rowId)),
    matchedCells: diff.matchedCells,
    counts: {
      ...diff.counts,
      added: cells.filter((cell) => cell.kind === 'new').length,
      changed: cells.filter((cell) => cell.kind === 'changed').length,
      decreased: cells.filter(isDecrease).length,
    },
  };
}

// Deep-copies parse.productionLines, keeping the 'capacity' metric value at a week ONLY when its
// `${rowId}:${weekStart}` key (rowId = lineRowId(line), same identity as PendingCell.rowId)
// appears in `cells` — the current (post-exclusion) diff the preview actually rendered. Every
// other week is nulled by the same rule: excluded cells, unchanged cells (buildImportDiff never
// puts an unchanged week into `cells` to begin with, so they carry no overwrite target anyway),
// and "needs attention" lines (no resolvable rowId — nothing they carry can ever match a cell)
// all fall out of the whitelist together. This is deliberately a whitelist, not a blacklist —
// "what the preview shows is exactly what gets applied" — so a line the preview never rendered
// anywhere can never sneak a value into the confirm payload. The backend's buildDrafts treats a
// null capacity value as "no proposal for this week" and skips it entirely
// (capacity-allocations.service.ts). Several sheet lines can share one calendar row (their weekly
// numbers sum server-side), so every line mapping to a non-whitelisted row+week gets nulled, not
// just the first one — otherwise the exclusion would only partially take effect.
export function buildApprovedProductionLines(parse: CapacityPlanParseResult, cells: readonly PendingCell[]): CapacityPlanProductionLine[] {
  const weekStarts = weekStartsOf(parse.weeks);
  const approvedKeys = new Set(cells.map((cell) => `${cell.rowId}:${cell.weekStart}`));

  return parse.productionLines.map((line) => {
    const rowId = lineRowId(line);
    return {
      ...line,
      metrics: line.metrics.map((metric) => {
        if (metric.key !== 'capacity') {
          return { ...metric, values: [...metric.values] };
        }
        return {
          ...metric,
          values: metric.values.map((value, index) => (rowId && approvedKeys.has(`${rowId}:${weekStarts[index]}`) ? value : null)),
        };
      }),
    };
  });
}
